import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Building2, CheckCircle, AlertTriangle, Clock, LayoutDashboard, Settings, Activity, X as XRayIcon, Shield, Plus } from 'lucide-react';
import { dashboardApi } from '../services/dashboardApi';
import './DashboardPage.css';

export default function DashboardPage() {
  const navigate = useNavigate();

  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: dashboardApi.getClinicMetrics,
  });

  if (isLoading) return <div className="dashboard-loading">Loading dashboard...</div>;
  if (error) return <div className="dashboard-error">{(error as Error).message || 'Error loading dashboard'}</div>;
  if (!metrics) return null;

  const pct = (n: number) =>
    metrics.totalClinics > 0 ? Math.round((n / metrics.totalClinics) * 100) : 0;

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div className="dashboard-title">
          <span className="dashboard-title-icon">
            <LayoutDashboard size={22} />
          </span>
          <div>
            <h1>Clinic Network Dashboard</h1>
            <p>Overview of clinic data and verification status</p>
          </div>
        </div>
      </header>

      <section className="metrics-grid">
        <MetricCard
          title="Total Clinics"
          value={metrics.totalClinics}
          icon={<Building2 size={22} />}
          color="blue"
          onClick={() => navigate('/clinics')}
        />
        <MetricCard
          title="Active Clinics"
          value={metrics.activeClinics}
          icon={<CheckCircle size={22} />}
          color="green"
          onClick={() => navigate('/clinics?status=1')}
          subtitle={`${pct(metrics.activeClinics)}% of total`}
        />
        <MetricCard
          title="Need Verification"
          value={metrics.clinicsNeedingVerification}
          icon={<AlertTriangle size={22} />}
          color="yellow"
          onClick={() => navigate('/verification')}
          urgent={metrics.overdueVerifications > 0}
        />
        <MetricCard
          title="Overdue Verification"
          value={metrics.overdueVerifications}
          icon={<Clock size={22} />}
          color="red"
          onClick={() => navigate('/verification')}
        />
      </section>

      {/* Capability Metrics Section */}
      {(metrics.clinicsWithPAT !== undefined || metrics.clinicsWithXRay !== undefined || metrics.clinicsWithFitTesting !== undefined) && (
        <section className="dashboard-section">
          <h2 className="section-title">Clinic Capabilities</h2>
          <div className="metrics-grid">
            {metrics.clinicsWithPAT !== undefined && (
              <MetricCard
                title="Physical Ability Testing"
                value={metrics.clinicsWithPAT}
                icon={<Activity size={22} />}
                color="blue"
                onClick={() => navigate('/clinics')}
                subtitle={`${pct(metrics.clinicsWithPAT)}% of clinics`}
                progress={pct(metrics.clinicsWithPAT)}
              />
            )}

            {metrics.clinicsWithXRay !== undefined && (
              <MetricCard
                title="X-Ray Capability"
                value={metrics.clinicsWithXRay}
                icon={<XRayIcon size={22} />}
                color="blue"
                onClick={() => navigate('/clinics')}
                subtitle={`${pct(metrics.clinicsWithXRay)}% of clinics`}
                progress={pct(metrics.clinicsWithXRay)}
              />
            )}

            {metrics.clinicsWithFitTesting !== undefined && (
              <MetricCard
                title="Fit Testing"
                value={metrics.clinicsWithFitTesting}
                icon={<Shield size={22} />}
                color="blue"
                onClick={() => navigate('/clinics')}
                subtitle={`${pct(metrics.clinicsWithFitTesting)}% of clinics`}
                progress={pct(metrics.clinicsWithFitTesting)}
              />
            )}
          </div>
        </section>
      )}

      <section className="dashboard-actions">
        <div className="panel">
          <div className="panel-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="action-buttons">
            <button className="action-btn primary" onClick={() => navigate('/clinics?create=true')}>
              <Plus size={16} />
              Add New Clinic
            </button>
            <button className="action-btn secondary" onClick={() => navigate('/verification')}>
              <AlertTriangle size={16} />
              Review Verifications
            </button>
            <button className="action-btn secondary" onClick={() => navigate('/settings')}>
              <Settings size={16} />
              Settings
            </button>
          </div>
        </div>

        <div className="panel verification-panel">
          <div className="panel-header">
            <h3>Verification Settings</h3>
          </div>
          <ul className="verification-facts">
            <li>
              <span>Verification cycle</span>
              <strong>{metrics.verificationSettings.verificationPeriodDays} days</strong>
            </li>
            <li>
              <span>Warning before due</span>
              <strong>{metrics.verificationSettings.warningDays} days</strong>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'yellow' | 'red';
  onClick: () => void;
  urgent?: boolean;
  subtitle?: string;
  /** 0-100; renders a progress bar when provided. */
  progress?: number;
}

function MetricCard({ title, value, icon, color, onClick, urgent, subtitle, progress }: Readonly<MetricCardProps>) {
  return (
    <div
      className={`metric-card ${color} ${urgent ? 'urgent' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="metric-card-head">
        <span className="metric-icon">{icon}</span>
        {urgent && <span className="urgent-badge" title="Overdue items need attention">!</span>}
      </div>
      <div className="metric-value">{value.toLocaleString()}</div>
      <div className="metric-label">{title}</div>
      {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      {progress !== undefined && (
        <div className="metric-progress" aria-hidden="true">
          <span className="metric-progress-fill" style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
        </div>
      )}
    </div>
  );
}
